import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import baseURL, { endPoints } from '../../Config';
import ReportExportButton from './ReportExportButton';
import { bonusSummaryExport, bonusDetailExport, canViewBonuses, readReportUser, statusLabel, routesLabel } from './bonusReportHelpers';
import './bonusesReport.css';

const percent = value => value === null || value === undefined ? 'Pendiente' : `${Number(value).toLocaleString('es-PA', { maximumFractionDigits: 2 })}%`;
const money = value => value === null || value === undefined ? 'Pendiente' : Number(value).toLocaleString('es-PA', { style: 'currency', currency: 'USD' });
const monthLabel = value => new Date(`${value}-15T12:00:00Z`).toLocaleDateString('es-PA', { month: 'long', year: 'numeric', timeZone: 'America/Panama' });
const severity = value => value === 'approved' ? 'success' : value === 'configuration_error' ? 'danger' : 'warning';

export default function BonusesReport() {
    const allowed = canViewBonuses(readReportUser());
    const [cycles, setCycles] = useState([]);
    const [cycleId, setCycleId] = useState('');
    const [month, setMonth] = useState('');
    const [operatorId, setOperatorId] = useState(null);
    const [report, setReport] = useState(null);
    const [expandedRows, setExpandedRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [liveRefresh, setLiveRefresh] = useState(0);
    const sequence = useRef(0);

    useEffect(() => {
        if (!allowed) { setLoading(false); return undefined; }
        const source = axios.CancelToken.source();
        let active = true;
        const user = JSON.parse(localStorage.getItem('adminUser') || localStorage.getItem('userDetails') || '{}');
        const headers = { authorization: `Bearer ${user?.data?.token || user?.data?.accessToken || ''}` };
        axios.get(`${baseURL}${endPoints.okrCycles}`, { headers, cancelToken: source.token }).then(response => {
            if (!active) return;
            const values = response.data?.data;
            if (!Array.isArray(values)) throw new Error('Respuesta de ciclos inválida');
            setCycles(values);
            const selected = values.find(cycle => cycle.status === 'active') || values[0];
            setCycleId(previous => values.some(cycle => cycle._id === previous) ? previous : selected?._id || '');
            if (!selected) setLoading(false);
        }).catch(err => { if (active && !axios.isCancel(err)) { setError('No se pudieron cargar los ciclos OKR.'); setLoading(false); } });
        return () => { active = false; source.cancel(); };
    }, [allowed, refresh]);

    useEffect(() => {
        if (!allowed || !cycleId) return undefined;
        const request = ++sequence.current;
        const source = axios.CancelToken.source();
        setLoading(true); setError(''); setReport(null); setExpandedRows(null);
        const user = JSON.parse(localStorage.getItem('adminUser') || localStorage.getItem('userDetails') || '{}');
        const params = new URLSearchParams({ cycleId });
        if (month) params.set('month', month);
        axios.get(`${baseURL}${endPoints.okrBonuses}?${params}`, {
            headers: { authorization: `Bearer ${user?.data?.token || user?.data?.accessToken || ''}` }, cancelToken: source.token
        }).then(response => {
            if (request !== sequence.current) return;
            const value = response.data?.data;
            if (!value || !Array.isArray(value.rows) || !Array.isArray(value.months)) throw new Error('Respuesta inválida');
            setReport(value);
        }).catch(err => {
            if (request === sequence.current && !axios.isCancel(err)) setError(err.response?.data?.message || 'No se pudieron cargar las bonificaciones.');
        }).finally(() => { if (request === sequence.current) setLoading(false); });
        return () => { sequence.current += 1; source.cancel(); };
    }, [allowed, cycleId, month, refresh, liveRefresh]);

    useEffect(() => {
        if (!allowed || report?.routeMode !== 'current') return undefined;
        const updateVisibleReport = () => { if (document.visibilityState !== 'hidden') setLiveRefresh(value => value + 1); };
        const timer = window.setInterval(updateVisibleReport, 60000);
        window.addEventListener('focus', updateVisibleReport);
        document.addEventListener('visibilitychange', updateVisibleReport);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('focus', updateVisibleReport);
            document.removeEventListener('visibilitychange', updateVisibleReport);
        };
    }, [allowed, report?.routeMode]);

    const rows = useMemo(() => (report?.rows || []).filter(row => !operatorId || row.userId === operatorId), [report, operatorId]);
    const summary = useMemo(() => bonusSummaryExport(rows, report), [rows, report]);
    const detail = useMemo(() => bonusDetailExport(rows, report), [rows, report]);
    const kinds = [{ key: 'route', title: 'Cumplimiento de ruta' }, { key: 'preventive', title: 'Preventivos de campo' }, { key: 'tickets', title: 'Tickets por cada 10 máquinas' }];
    const indicatorBody = (row, kind) => {
        const indicator = row.indicators.find(item => item.kind === kind);
        return indicator ? <span>{percent(indicator.achievementPercentage)}<small className="bonus-weight">Peso: {percent(indicator.weightPercentage)}</small></span> : 'Sin configurar';
    };
    const expanded = row => <div className="bonus-detail">
        <h4>Detalle semanal de {row.name}</h4>
        {row.issues.map(issue => <Message key={issue} severity="error" text={issue} />)}
        <p>Se usan los puntos finales guardados en OKR, incluidos sus ajustes. Las semanas pertenecen al mes en que terminan.</p>
        <DataTable value={row.weekly} dataKey="weekNumber" responsiveLayout="scroll">
            <Column header="Semana OKR" body={week => `Semana ${week.weekNumber}`} />
            <Column header="Fechas" body={week => `${week.startDate} – ${week.endDate}`} />
            {kinds.map(kind => <Column key={kind.key} header={kind.title} body={week => {
                const indicator = week.details.find(item => item.kind === kind.key);
                return <span>{percent(indicator?.achievementPercentage)}<small className="bonus-weight">{indicator ? statusLabel(indicator.status) : 'Sin configurar'}</small></span>;
            }} />)}
            <Column header="Puntos obtenidos / posibles" body={week => `${week.earnedPoints ?? 'Pendiente'} / ${week.expectedPoints}`} />
            <Column header="Resultado ponderado" body={week => percent(week.achievementPercentage)} />
        </DataTable>
        <p>Resultado mensual = promedio de los resultados semanales completos. Bono = resultado mensual × {money(row.maximum)}, con ese mismo tope.</p>
        <a href="#/okr">Consultar registros originales en OKR</a>
    </div>;

    if (!allowed) return <Message severity="warn" text="Sin acceso a Analítica y Reportes." />;
    return <section className="bonuses-report" aria-label="Bonificaciones de operadores">
        <h3>Bonificaciones</h3>
        <p>Operadores actuales y operadores del mes identificados por sus registros OKR. Se usan los resultados guardados en OKR.</p>
        <div className="bonus-filters">
            <label>Ciclo OKR<Dropdown inputId="bonus-cycle" aria-label="Ciclo OKR" value={cycleId} options={cycles.map(cycle => ({ label: cycle.name, value: cycle._id }))} onChange={event => { setReport(null); setMonth(''); setOperatorId(null); setCycleId(event.value); }} placeholder="Selecciona un ciclo" /></label>
            <label>Mes<Dropdown inputId="bonus-month" aria-label="Mes" value={month || report?.month || ''} options={(report?.months || []).map(item => ({ label: monthLabel(item.value), value: item.value }))} onChange={event => { setReport(null); setMonth(event.value); }} disabled={loading || !report} /></label>
            <label>Operador<Dropdown inputId="bonus-operator" aria-label="Operador" value={operatorId} options={(report?.rows || []).map(row => ({ label: row.name, value: row.userId }))} onChange={event => setOperatorId(event.value)} placeholder="Todos" showClear filter disabled={loading || !report} /></label>
            <Button label="Actualizar" icon="pi pi-refresh" onClick={() => setRefresh(value => value + 1)} disabled={loading} />
        </div>
        {error && <Message severity="error" text={error} />}
        {loading && <p role="status">Cargando bonificaciones…</p>}
        {!loading && !error && cycles.length === 0 && <Message severity="info" text="No hay ciclos OKR disponibles." />}
        {report && <>
            <div className="bonus-summary" aria-label="Resumen del mes">
                <div><span>Total calculado</span><strong>{money(report.totals.calculatedBonus)}</strong><small>Incluye provisionales completos</small></div>
                <div><span>Total aprobado</span><strong>{money(report.totals.approvedBonus)}</strong><small>Mes finalizado y registros aprobados</small></div>
                <div><span>Operadores elegibles</span><strong>{report.totals.operators}</strong></div>
                <div><span>Operadores con pendientes</span><strong>{report.totals.pending}</strong></div>
            </div>
            <p>Máximo mensual: <b>{money(report.maximum)}</b>. Calendario de OKR en Panamá. Cada semana se asigna al mes de su fecha de cierre.</p>
            <p>{report.routeMode === 'historical'
                ? 'Las rutas del mes provienen de las asignaciones de Syncro guardadas en los OKR. Se indica cuando el historial está incompleto o no está disponible.'
                : report.routeMode === 'current'
                    ? 'Rutas actuales de las máquinas asignadas en Syncro. Se actualizan cada minuto y al volver a esta ventana. El Excel conserva los datos del momento de exportación.'
                    : 'Las asignaciones de rutas futuras no se anticipan.'}</p>
            <div className="bonus-exports"><ReportExportButton rows={summary} filename={`Bonificaciones_${report.month}`} disabled={loading || !rows.length} /><span>Resumen</span><ReportExportButton rows={detail} filename={`Bonificaciones_detalle_${report.month}`} disabled={loading || !detail.length} /><span>Detalle semanal</span></div>
            <DataTable value={rows} dataKey="userId" expandedRows={expandedRows} onRowToggle={event => setExpandedRows(event.data)} rowExpansionTemplate={expanded} responsiveLayout="scroll" emptyMessage="No hay operadores elegibles para esta selección." paginator rows={15}>
                <Column expander style={{ width: '3rem' }} />
                <Column field="name" header="Operador" sortable />
                <Column header={report.routeMode === 'historical' ? 'Rutas del mes' : 'Rutas actuales'} body={routesLabel} />
                {kinds.map(kind => <Column key={kind.key} header={kind.title} body={row => indicatorBody(row, kind.key)} />)}
                <Column field="achievementPercentage" header="Resultado mensual" body={row => percent(row.achievementPercentage)} sortable />
                <Column field="bonus" header="Bono calculado" body={row => <strong>{money(row.bonus)}</strong>} sortable />
                <Column header="Estado" body={row => <Tag severity={severity(row.status)} value={statusLabel(row.status)} />} />
            </DataTable>
            <p>Los registros faltantes, rechazados o en borrador se muestran como pendientes. El reporte refleja los OKR actuales; no registra pagos.</p>
        </>}
    </section>;
}
